import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../modals/ConfirmationModal";
import { Recommendation } from "../../types/control";

interface RecommendationActionsProps {
  recommendation: Recommendation;
  busy: boolean;
  size?: "small" | "medium";
  onAccept: (note?: string) => void | Promise<void>;
  onReject: (note?: string) => void | Promise<void>;
  onRetry: () => void | Promise<void>;
}

/**
 * Botonera de decisión de un aviso (Aceptar / Denegar / Reintentar).
 * Aceptar pide confirmación porque aplica el cambio en DV360 de verdad.
 * Denegar abre un campo de nota opcional. Reintentar solo aparece para FAILED.
 */
const RecommendationActions: React.FC<RecommendationActionsProps> = ({
  recommendation,
  busy,
  size = "medium",
  onAccept,
  onReject,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [confirmAccept, setConfirmAccept] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  const isFailed = recommendation.applyStatus === "FAILED";

  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      <Button
        variant="contained"
        color="success"
        size={size}
        startIcon={busy ? <CircularProgress size={16} /> : <CheckIcon />}
        disabled={busy}
        onClick={() => setConfirmAccept(true)}
        sx={{ textTransform: "none", color: "white" }}
      >
        {t("control.actions.accept")}
      </Button>
      <Button
        variant="outlined"
        color="error"
        size={size}
        startIcon={<CloseIcon />}
        disabled={busy}
        onClick={() => setRejectOpen(true)}
        sx={{ textTransform: "none" }}
      >
        {t("control.actions.deny")}
      </Button>
      {isFailed && (
        <Button
          variant="outlined"
          color="warning"
          size={size}
          startIcon={busy ? <CircularProgress size={16} /> : <ReplayIcon />}
          disabled={busy}
          onClick={() => onRetry()}
          sx={{ textTransform: "none" }}
        >
          {t("control.actions.retry")}
        </Button>
      )}

      {/* Confirmación de Aceptar (aplica en DV360) */}
      <ConfirmationModal
        open={confirmAccept}
        title={t("control.actions.acceptTitle")}
        message={t("control.actions.acceptMessage", {
          line: recommendation.lineName,
        })}
        confirmLabel={t("control.actions.acceptConfirm")}
        cancelLabel={t("common.cancel")}
        isLoading={busy}
        onCancel={() => setConfirmAccept(false)}
        onConfirm={async () => {
          await onAccept();
          setConfirmAccept(false);
        }}
      />

      {/* Denegar con nota opcional */}
      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">{t("control.actions.denyTitle")}</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography sx={{ mb: 2 }}>{t("control.actions.denyHelp")}</Typography>
          <TextField
            label={t("control.actions.noteLabel")}
            fullWidth
            multiline
            minRows={2}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={busy}
            sx={{ color: (theme) => theme.palette.neutral[600] }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            sx={{ color: "white" }}
            onClick={async () => {
              await onReject(rejectNote.trim() || undefined);
              setRejectOpen(false);
              setRejectNote("");
            }}
          >
            {t("control.actions.deny")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecommendationActions;
